import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * A simplified hook for polling data at dynamic intervals
 * @param {Function} pollingFunction - The function to call on each poll interval
 * @param {Function} calculateInterval - Function that returns the polling interval in ms
 * @param {Array} dependencies - Dependencies to watch for interval recalculation
 * @param {boolean} immediate - Whether to run the polling function immediately
 * @param {number} defaultInterval - Default polling interval in ms
 * @returns {number} Current polling interval in ms
 */
export const usePolling = (
  {
    pollingFunction,
    calculateInterval,
    immediate = true,
    defaultInterval = 60000,
  },
  dependencies = []
) => {
  const [interval, setInterval] = useState(defaultInterval);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const pollingFunctionRef = useRef(pollingFunction);
  const calculateIntervalRef = useRef(calculateInterval);

  // Keep refs updated with the latest functions
  useEffect(() => {
    pollingFunctionRef.current = pollingFunction;
  }, [pollingFunction]);

  useEffect(() => {
    calculateIntervalRef.current = calculateInterval;
  }, [calculateInterval]);

  // Function to execute the poll
  const executePoll = useCallback(() => {
    if (isMountedRef.current && pollingFunctionRef.current) {
      pollingFunctionRef.current();
    }
  }, [interval]); // Depends on interval for logging, but function itself uses ref

  // Effect to manage the polling loop and interval calculation
  useEffect(() => {
    isMountedRef.current = true;

    // Function to schedule the next poll
    const scheduleNextPoll = (currentInterval) => {
      // Clear any existing timeout before scheduling a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        executePoll();
        // Use the interval value from the closure when scheduling the *next* poll
        if (isMountedRef.current) {
          scheduleNextPoll(currentInterval);
        }
      }, currentInterval); // Use the interval passed to the function
    };

    // Calculate new interval based on dependencies
    let newInterval = defaultInterval;
    if (calculateIntervalRef.current) {
      newInterval = calculateIntervalRef.current();
      // Ensure interval is a positive number, fall back to default if not
      if (typeof newInterval !== 'number' || newInterval <= 0) {
        newInterval = defaultInterval;
      }
      setInterval(newInterval); // Update state for return value and logging
    } else {
      newInterval = interval; // Use current state interval if no calculate function
    }

    // Run immediately if requested or if dependencies changed
    if (immediate || dependencies.length > 0) {
      // Check if dependencies array itself changed reference or is not empty
      executePoll();
    }

    // Start the polling loop with the calculated interval
    scheduleNextPoll(newInterval);

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // Rerun effect when dependencies array changes, immediate flag changes, or defaultInterval changes
    // We don't need interval in deps because scheduleNextPoll uses the calculated newInterval
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dependencies), immediate, defaultInterval, executePoll]); // executePoll dependency ensures restart if interval changes

  // Effect purely for cleanup on unmount (redundant with the above, but safe)
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return interval;
};

export default usePolling;
